"use client";

import { useEffect, useState } from "react";

/** Static fallback used until the admin-uploaded PDF loads (or if none exists). */
export const FALLBACK_WEB_USER_GUIDE = "/documents/web-user-flow.pdf";

export interface WebUserGuide {
  /** Ready-to-use URL, cache-busted with a version token when admin-uploaded. */
  url: string;
  name: string | null;
  /** True once the admin-uploaded guide has loaded (vs the static fallback). */
  isCustom: boolean;
}

/**
 * Reads the admin-managed web user guide and returns a cache-busted URL.
 *
 * The stored PDF lives at a fixed path (stable URL for the mobile app), so on the
 * web we append `?v=<upload-time>` to defeat browser/CDN caching — combined with
 * the object's max-age=0, a re-uploaded guide is reflected without a hard refresh.
 */
export function useWebUserGuide(): { guide: WebUserGuide; loading: boolean } {
  const [guide, setGuide] = useState<WebUserGuide>({
    url: FALLBACK_WEB_USER_GUIDE,
    name: null,
    isCustom: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetch("/api/user-guides")
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!active) return;
        const url = json?.data?.web?.url;
        const updatedAt = json?.data?.web?.updated_at;
        const name = json?.data?.web?.name ?? null;
        if (typeof url === "string" && url) {
          const version = updatedAt ? new Date(updatedAt).getTime() : null;
          setGuide({ url: version ? `${url}?v=${version}` : url, name, isCustom: true });
        }
      })
      .catch(() => {
        /* keep fallback */
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { guide, loading };
}
